import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

// Token event emitter
class TokenEventEmitter extends EventTarget {}
export const tokenEvents = new TokenEventEmitter();

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Include cookies in requests
});

// In-memory token storage
let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
  tokenEvents.dispatchEvent(new CustomEvent('tokenRefreshed', { detail: { token } }));
};

export const getAccessToken = () => {
  return accessToken;
};

export const clearAccessToken = () => {
  accessToken = null;
};

// Request interceptor to attach access token to headers
axiosInstance.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh on 401 errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const response = await axios.post(
          `${API_BASE}/refresh_token`,
          {},
          { withCredentials: true }
        );

        if (response.data.success && response.data.accessToken) {
          // Update access token
          setAccessToken(response.data.accessToken);

          // Retry the original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${response.data.accessToken}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear token (user stays on page but logged out)
        clearAccessToken();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
